<template>
  <shared-product-provide
    v-if="account && Object.keys(account).length > 0"
    :product="product"
    :fromModule="fromModule"
    :toModule="toModule"
  >
    <panel>
      <div class="l-row">
        <form-group :label="$t('tikTokShop.shop.label')" :required="true" :disabled="disabledInputs()" :error="getError('selectedAuthorizedShop',  product.productId)">
          <select v-model="selectedAuthorizedShop" class="form-select">
            <option :value="null" disabled>Selecione uma loja</option>
            <option v-for="authorizedShop in authorizedShops" :key="authorizedShop.identifier" :value="authorizedShop">
              {{ authorizedShop.nickname }}
            </option>
          </select>
        </form-group>

        <form-group :label="$t('tikTokShop.warehouse.label')" :required="true" :disabled="disabledInputs()" :error="getError('selectedWarehouse',  product.productId)">
          <select v-model="selectedWarehouse" class="form-select">
            <option :value="null" disabled>Selecione um centro de distribuição</option>
            <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse">
              {{ warehouse.name }}
            </option>
          </select>
        </form-group>
      </div>
    </panel>

    <div class="c-box" v-if="!loading">
      <loader :loading="sending" :label="getCountVariations()"/>

      <header class="c-box__heading" :class="{disabledHeader: product.status === 'cannotSend'}" @click="toggleProductInfo()">
        <span v-once>{{ product.title }}</span>
        <i :class="toggleIconClass()" class="u-floatRight icon"></i>
      </header>

      <div class="c-box__body" v-show="show && !everyVariationWithErrorOrSent">
        <div class="c-box__inner">

          <panel v-if="product" title="Informações gerais do produto">
            <div slot="header-right">
              <button :disabled="addingImage" :class="{ 'is-loading': addingImage }" @click="addingImage = true"
                      class="hint hint--left btn btn--info" data-label="Adicionar uma imagem em uma ou mais variações">
                Adicionar Imagem
              </button>
              <modal-add-image :visible="addingImage" :variations="product.variations"
                               :callback="uploadedImage" @close="close => addingImage = close"
                               :module="moduleUploadImageModal"/>
            </div>

            <div class="l-row">
              <form-group label="Título" :columns="12" slot="product" :error="getError('title', product.productId)" :required="true">
                <input class="form-input" type="text" v-model="product.title" :disabled="disabledInput('title')">
                <small v-if="maxTitleLengthDI !== null" class="form-help" :class="titleLength">{{ getTitleLengthLabel() }}</small>
              </form-group>

              <form-group label="Descrição" :columns="12" slot="product" :error="getError('description', product.productId)" :required="true">
                <textarea class="form-textarea" rows="8" v-model="product.description" :disabled="disabledInput('description')"/>
                <small v-if="maxDescriptionLengthDI !== null" class="form-help" :class="descriptionLength">{{ getDescriptionLengthLabel() }}</small>
              </form-group>
              <slot/>
            </div>

            <div class="l-row">
              <form-group label="Buscar categorias" :required="true" :disabled="disabledInputs()" :error="getError('selectedCategory',  product.productId)">
                <input v-model="categorySearch" class="form-input" placeholder="Digite para buscar..."
                       @blur="handleCategorySearch" @keyup.enter="handleCategorySearch"/>
              </form-group>

              <form-group label="Categoria do produto" :required="true" :disabled="disabledInputs()" :error="getError('selectedCategory',  product.productId)">
                <loader :loading="loadingCategories"/>
                <div class="select-wrapper">
                  <select v-model="selectedCategory" class="form-select" :disabled="loadingCategories">
                    <option disabled :value="null">{{ categoryPlaceholder }}</option>
                    <option v-for="category in categories" :key="category.id" :value="category">
                      {{ category.name }}
                    </option>
                  </select>
                </div>
              </form-group>

              <form-group label="Buscar marcas" :required="true" :disabled="disabledInputs()" :error="getError('selectedBrand', product.productId)">
                <input v-model="brandSearch" class="form-input" placeholder="Digite para buscar..."
                      @blur="handleBrandSearch" @keyup.enter="handleBrandSearch"/>
              </form-group>

              <form-group label="Marca do produto" :required="true" :disabled="disabledInputs()" :error="getError('selectedBrand', product.productId)">
                <loader :loading="loadingBrands"/>
                <div class="select-wrapper">
                  <select v-model="selectedBrand" class="form-select" :disabled="loadingBrands">
                    <option disabled :value="null">Selecione uma marca</option>
                    <option v-for="brand in brands" :key="brand.id" :value="brand">
                      {{ brand.name }}
                    </option>
                  </select>
                </div>
              </form-group>
            </div>

            <div class="l-row">
              <form-group :label="$t('crossDocking.label')" :error="getError('crossDocking', product.productId)">
                <input type="number" class="form-input" v-model="crossDocking" :disabled="disabledInputs()">
                <small class="form-help">(Em dias)</small>
              </form-group>
            </div>
          </panel>

          <panel title="Requisitos da categoria">
            <loader :loading="loadingCategoryRules"/>

            <div v-if="!selectedCategory && !loadingCategoryRules" class="l-row">
              <div class="l-col-12@md">
                <p class="placeholder-msg">Selecione a categoria para exibir os requisitos da categoria.</p>
              </div>
            </div>

            <div class="l-row" v-else-if="hasCategoryRequirements && !loadingCategoryRules">
              <template v-if="must('package_dimension')">
                <div class="l-col-12@md">
                  <div class="divider"><span>Dimensões do pacote</span></div>
                </div>

                <form-group label="Comprimento" :columns="3" :required="true" :error="getError('requirements.package_dimension.length',  product.productId)">
                  <input type="number" min="0" class="form-input" v-model.number="requirements.package_dimension.length" placeholder="Ex: 20" :disabled="disabledInputs()"/>
                </form-group>
                <form-group label="Largura" :columns="3" :required="true" :error="getError('requirements.package_dimension.width',  product.productId)">
                  <input type="number" min="0" class="form-input" v-model.number="requirements.package_dimension.width" placeholder="Ex: 15" :disabled="disabledInputs()"/>
                </form-group>
                <form-group label="Altura" :columns="3" :required="true" :error="getError('requirements.package_dimension.height',  product.productId)">
                  <input type="number" min="0" class="form-input" v-model.number="requirements.package_dimension.height" placeholder="Ex: 10" :disabled="disabledInputs()"/>
                </form-group>
                <form-group label="Peso do pacote" :columns="3" :required="true" :error="getError('requirements.package_dimension.packageWeight',  product.productId)">
                  <input type="number" min="0" class="form-input" v-model.number="requirements.package_dimension.packageWeight" placeholder="Ex: 0.5" :disabled="disabledInputs()"/>
                  <small class="form-help">(Peso em gramas)</small>
                </form-group>
                <form-group label="Unidade" :columns="3" :required="true" :error="getError('requirements.package_dimension.unit',  product.productId)">
                  <select class="form-select" v-model="requirements.package_dimension.unit" :disabled="disabledInputs()">
                    <option value="CM">CM</option>
                    <option value="INCH">INCH</option>
                  </select>
                </form-group>
              </template>

              <form-group v-if="must('manufacturer')" label="Fabricante" :columns="6" :required="true" :error="getError('requirements.manufacturer',  product.productId)">
                <input class="form-input" type="text" v-model.trim="requirements.manufacturer" placeholder="Informe o fabricante" :disabled="disabledInputs()"/>
              </form-group>

              <form-group v-if="must('epr')" label="EPR (nº/identificação)" :columns="6" :required="true" :error="getError('requirements.epr',  product.productId)">
                <input class="form-input" type="text" v-model.trim="requirements.epr" placeholder="Informe o EPR exigido" :disabled="disabledInputs()"/>
              </form-group>

              <form-group v-if="must('cod')" label="COD (Cash on Delivery)" :columns="6" :required="true" :error="getError('requirements.cod',  product.productId)">
                <select class="form-select" v-model="requirements.cod" :disabled="disabledInputs()">
                  <option :value="true">Sim</option>
                  <option :value="false">Não</option>
                </select>
              </form-group>

              <template v-if="must('responsible_person')">
                <div class="l-col-12@md">
                  <div class="divider"><span>Responsável pelo produto</span></div>
                </div>
                <form-group label="Nome" :columns="4">
                  <input class="form-input" type="text" v-model.trim="requirements.responsible_person.name" placeholder="Nome completo" :disabled="disabledInputs()"/>
                </form-group>
                <form-group label="Telefone" :columns="4">
                  <input class="form-input" type="text" v-model.trim="requirements.responsible_person.phone" placeholder="(DDD) 00000-0000" :disabled="disabledInputs()"/>
                </form-group>
                <form-group label="E-mail" :columns="4">
                  <input class="form-input" type="email" v-model.trim="requirements.responsible_person.email" placeholder="email@exemplo.com" :disabled="disabledInputs()"/>
                </form-group>
              </template>

              <template v-if="must('size_chart')">
                <div class="l-col-12@md">
                  <div class="divider"><span>Guia de tamanho</span></div>
                </div>

                <form-group label="Guia de tamanho" :columns="3" :required="true" :error="getError('requirements.size_chart',  product.productId)">
                  <loader :loading="loadingSizeCharts"/>
                  <div v-if="!loadingSizeCharts">
                    <div class="select-wrapper" v-if="sizeCharts.length">
                      <select v-model="selectedSizeChart" class="form-select" :disabled="disabledInputs()">
                        <option :value="null" disabled>Selecione uma guia de tamanho</option>
                        <option v-for="chart in sizeCharts" :key="chart.template_id" :value="chart">
                          {{ chart.template_name }}
                        </option>
                      </select>
                    </div>
                    <p v-else class="placeholder-msg">A categoria precisa de uma guia de tamanho. Nenhuma guia de tamanho cadastrada na loja. Por favor, cadastre uma guia de tamanho no Marketplace.</p>

                    <img
                      v-if="selectedSizeChart && selectedSizeChart.images && selectedSizeChart.images.length"
                      :src="selectedSizeChart.images[0].url"
                      :alt="selectedSizeChart.template_name"
                      class="size-chart-preview"
                    />
                  </div>
                </form-group>
              </template>

              <template v-if="productCertRulesRequired.length">
                <div class="l-col-12@md">
                  <div class="divider"><span>Certificações exigidas</span></div>
                </div>

                <div class="l-col-12@md" v-for="(cert, idx) in requirements.product_certifications" :key="'cert-'+idx">
                  <div class="c-box__inner" style="padding:8px 0">
                    <strong>{{ cert.name || ('Certificação #' + (idx+1)) }}</strong>

                    <div class="l-row">
                      <form-group label="Detalhes do documento" :columns="6" :required="true" :error="getError(`requirements.product_certifications[${idx}].document_details`,  product.productId)">
                        <input class="form-input" type="text" v-model.trim="cert.document_details" placeholder="Resumo/identificação do documento" :disabled="disabledInputs()"/>
                      </form-group>

                      <form-group label="Data de expiração" :columns="3" v-if="cert.expiration_date && cert.expiration_date.is_required" :required="true" :error="getError(`requirements.product_certifications[${idx}].expiration_date.value`,  product.productId)">
                        <input class="form-input" type="date" v-model="cert.expiration_date.value" :disabled="disabledInputs()"/>
                      </form-group>

                      <form-group label="URL da imagem/extrato" :columns="6" v-if="cert.sample_image_url" :required="true" :error="getError(`requirements.product_certifications[${idx}].sample_image_url_user`, product.productId)">
                        <input class="form-input" type="text" v-model.trim="cert.sample_image_url_user" placeholder="https://link-da-imagem-ou-arquivo" :disabled="disabledInputs()"/>
                      </form-group>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <div v-else-if="selectedCategory && !loadingCategoryRules" class="l-row">
              <div class="l-col-12@md">
                <p class="placeholder-msg">Esta categoria não possui requisitos obrigatórios.</p>
              </div>
            </div>
          </panel>

          <panel title="Atributos">
            <loader :loading="loadingAttributes"/>

            <div v-if="!selectedCategory && !loadingAttributes" class="l-row">
              <div class="l-col-12@md">
                <p class="placeholder-msg">Selecione a categoria para exibir os atributos.</p>
              </div>
            </div>

            <div class="l-row" v-else-if="attributes.length">
              <form-group
                v-for="attribute in productOnlyAttributes"
                :key="attribute.id"
                :label="attribute.name"
                :required="attribute.isRequired"
                :columns="6"
                :error="getError(String(attribute.id), product.productId)"
              >
                <multiselect
                  v-if="attribute.isMultipleSelection"
                  v-model="attribute.value"
                  :options="attribute.values || []"
                  track-by="id"
                  label="name"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :hide-selected="true"
                  :taggable="!!attribute.isCustomizable"
                  @tag="val => addCustomTagProduct(attribute, val)"
                  :placeholder="getAttributePlaceholder(attribute)"
                  :disabled="disabledInputs()"
                />

                <select
                  v-else-if="attribute.values && attribute.values.length && attribute.isMultipleSelection === false"
                  v-model="product.attributes[attribute.id]"
                  class="form-select"
                  :disabled="disabledInputs()"
                >
                  <option :value="null" disabled>{{ getAttributePlaceholder(attribute) }}</option>
                  <option v-for="value in attribute.values" :key="value.id" :value="value">
                    {{ value.name }}
                  </option>
                </select>

                <input
                  v-else
                  type="text"
                  class="form-input"
                  v-model="product.attributes[attribute.id]"
                  :placeholder="getAttributePlaceholder(attribute)"
                  :disabled="disabledInputs()"
                />
              </form-group>
            </div>

            <div v-else-if="selectedCategory && !loadingAttributes" class="l-row">
              <div class="l-col-12@md">
                <p class="placeholder-msg">Esta categoria não possui atributos configuráveis.</p>
              </div>
            </div>
          </panel>

          <div class="divider">
            <span>Informações das variações do produto</span>
          </div>

          <div class="l-row vertical-flex">
            <div class="l-col-12@md" v-for="variation in product.variations" :key="variation.sku || variation.id">
              <MarketplaceProvideVariationTikTokShop
                :variation="variation"
                :from-module="fromModule"
                :to-module="toModule"
                :variation-attributes="variationAttributes"
                :loading-variation-attributes="loadingAttributes"
                :account="{ identifier: account.identifier }"
              />
            </div>
          </div>

        </div>
      </div>

    </div>
  </shared-product-provide>
</template>

<script>
import Panel from '@/components/template/Panel'
import { quillEditor } from 'vue-quill-editor'
import ModalAddImage from '@/components/marketplace/shared/provide/ModalAddImage'
import { mapActions, mapGetters } from 'vuex'
import api from '@/api'
import notifier from '@/notifier'
import { findCharacteristicValue, getCharacteristicLabel, getProductCharacteristics } from '@/components/marketplace/shared/provide/presetCharacteristics'
import FormGroup from '@/components/template/FormGroup'
import Multiselect from 'vue-multiselect'
import Loader from '@/components/template/Loader'
import MarketplaceProvideVariationTikTokShop from '@/components/marketplace/tiktokshop/provide/Variation'

export default {
  inject: ['maxDescriptionLengthDI', 'moduleDI', 'maxTitleLengthDI'],
  provide: {
    moduleDI: { type: 'marketplace', identifier: 'tikTokShop' }
  },
  data: () => ({
    loading: false,
    maxTitleLengthDI: 60,
    maxDescriptionLengthDI: 2000,
    authorizedShops: [],
    selectedAuthorizedShop: null,
    warehouses: [],
    selectedWarehouse: null,

    categorySearch: '',
    categories: [],
    selectedCategory: null,
    loadingCategories: false,

    brandSearch: '',
    brands: [],
    selectedBrand: null,
    loadingBrands: false,

    categoryRules: [],
    loadingCategoryRules: false,
    sizeCharts: [],
    selectedSizeChart: null,
    loadingSizeCharts: false,
    requirements: {
      package_dimension: { length: null, width: null, height: null, packageWeight: null, unit: 'CM' },
      manufacturer: '',
      epr: '',
      cod: null,
      responsible_person: { name: '', phone: '', email: '' },
      size_chart: '',
      product_certifications: []
    },

    attributes: [],
    loadingAttributes: false,

    show: false,
    addingImage: false,
    crossDocking: 0
  }),
  props: {
    account: { type: Object, required: true },
    product: { type: Object, required: true },
    fromModule: { type: Object, required: true },
    toModule: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    disabledInputList: { type: Array, default: () => [] }
  },
  async mounted () {
    this.removeHtml()

    // Só consulta authorizedshops se há uma conta válida selecionada
    if (this.account && this.account.identifier) {
      await this.getAuthorizedShops()
    }

    if (!this.product.attributes || Array.isArray(this.product.attributes) || typeof this.product.attributes !== 'object') {
      this.$set(this.product, 'attributes', {})
    }

    if (this.product.categories[0].name.split(' ')[0]) {
      this.categorySearch = this.product.categories[0].name.split(' ')[0]
      this.handleCategorySearch()
    }

    this.$parent.loadingProducts(false)
  },
  methods: {
    ...mapActions('errors', ['addError', 'clearErrors']),
    disabledInput (input) {
      if (!this.disabledInputList.length) return false
      return this.disabledInputList.indexOf(input) !== -1
    },
    toggleProductInfo () {
      if (['sent', 'error', 'cannotSend'].includes(this.product.status)) return
      this.show = !this.show
    },
    toggleIconClass () {
      return { 'icon-circle-up': this.show, 'icon-circle-down': !this.show || this.everyVariationWithErrorOrSent }
    },
    removeHtml () {
      const doc = new DOMParser().parseFromString(this.product.description, 'text/html')
      doc.querySelectorAll('li').forEach(li => {
        li.textContent = '  • ' + li.textContent
      })
      let text = ''
      const walker = doc.createTreeWalker(doc.body, 4 | 1)
      let node = walker.nextNode()
      while (node) {
        if (node.nodeType === 3) {
          text += node.textContent
        } else if (node.nodeType === 1 && /^(P|DIV|H[1-6]|LI|BR)$/.test(node.tagName)) {
          text += '\n'
        }
        node = walker.nextNode()
      }
      text = text
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
      this.product.description = text
    },
    getDescriptionLengthLabel () { return `${this.product.description.length}/${this.maxDescriptionLengthDI}` },
    getTitleLengthLabel () { return `${this.product.title.length}/${this.maxTitleLengthDI}` },
    getCountVariations () {
      const selectedVariations = this.product.variations.filter(v => v.selected)
      const sent = selectedVariations.filter(v => v.status !== 'sending').length
      return `${sent}/${selectedVariations.length} variações enviadas`
    },
    addCustomTagProduct (attribute, val) {
      if (!attribute.values) this.$set(attribute, 'values', [])
      const exists = attribute.values.some(v => String(v.id) === String(val))
      if (!exists) attribute.values.push({ id: String(val), name: String(val), custom: true })
    },
    uploadedImage (urls, variations) {
      for (const v of variations) {
        const found = this.product.variations.find(x => x.sku === v.sku)
        if (!found) continue
        for (const u of urls) found.images.push(u)
      }
      variations.splice(0, variations.length)
    },
    must (key) {
      const r = this.categoryRules && this.categoryRules[key]
      if (!r) return false
      const supported = (r.is_supported === undefined) ? true : !!r.is_supported
      return supported && !!r.is_required
    },
    initRequirementsFromRules () {
      this.requirements.product_certifications = []

      const certs = (this.categoryRules && this.categoryRules.product_certifications) || []
      if (Array.isArray(certs) && certs.length) {
        this.requirements.product_certifications = certs
          .filter(c => c && (c.is_required === true))
          .map(c => ({
            id: c.id,
            name: c.name,
            is_required: true,
            document_details: '',
            sample_image_url_user: '',
            expiration_date: c.expiration_date ? { is_required: !!c.expiration_date.is_required, value: '' } : null,
            sample_image_url: c.sample_image_url || '',
            requirement_conditions: c.requirement_conditions || []
          }))
      }

      if (this.must('package_dimension') && !this.requirements.package_dimension.unit) {
        this.$set(this.requirements.package_dimension, 'unit', 'CM')
      }

      if (this.must('cod') && this.requirements.cod === null) {
        this.requirements.cod = false
      }
    },
    async getAuthorizedShops () {
      this.loading = true
      try {
        this.authorizedShops = await api.tikTokShop.getAuthorizedShops(this.account.identifier)
        if (this.authorizedShops.length === 1) this.selectedAuthorizedShop = this.authorizedShops[0]
      } catch (_) {
        notifier.error('Não foi possível buscar as lojas autorizadas nesta conta.')
        this.authorizedShops = []
      } finally {
        this.loading = false
      }
    },
    async getWarehouses () {
      this.loading = true
      try {
        this.warehouses = await api.tikTokShop.getWarehouses(this.account.identifier, this.selectedAuthorizedShop.cipher)
        if (this.warehouses.length === 1) this.selectedWarehouse = this.warehouses[0]
      } catch (_) {
        notifier.error('Não foi possível buscar os centros de distribuição para essa loja.')
        this.warehouses = []
      } finally {
        this.loading = false
      }
    },
    resetCategoryContext () {
      this.selectedCategory = null
      this.categories = []
      this.selectedBrand = null
      this.brands = []
      this.categoryRules = []
      this.loadingCategoryRules = false
      this.sizeCharts = []
      this.selectedSizeChart = null
      this.loadingSizeCharts = false
      this.attributes = []
      this.loadingAttributes = false

      this.requirements = {
        package_dimension: { length: null, width: null, height: null, packageWeight: null, unit: 'CM' },
        manufacturer: '',
        epr: '',
        cod: null,
        responsible_person: { name: '', phone: '', email: '' },
        size_chart: '',
        product_certifications: []
      }

      if (!this.product.attributes || Array.isArray(this.product.attributes) || typeof this.product.attributes !== 'object') {
        this.$set(this.product, 'attributes', {})
      } else {
        Object.keys(this.product.attributes).forEach(k => this.$delete(this.product.attributes, k))
      }

      if (Array.isArray(this.product.variations)) {
        this.product.variations.forEach(v => {
          if (!v.attributes) {
            this.$set(v, 'attributes', {})
          }
        })
      }
    },
    resetBrandContext () {
      this.brands = []
      this.selectedBrand = null
    },
    async handleCategorySearch () {
      if (!this.categorySearch || this.categorySearch.length < 3) return
      this.resetCategoryContext()
      await this.fetchCategoriesByKeyword()
    },
    async handleBrandSearch () {
      if (!this.brandSearch || this.brandSearch.length < 3) return
      this.resetBrandContext()
      await this.fetchBrandsByCategory()
    },
    async fetchCategoriesByKeyword () {
      this.loadingCategories = true
      try {
        this.categories = await api.tikTokShop.getCategories(
          this.account.identifier,
          this.selectedAuthorizedShop.cipher,
          this.categorySearch
        )
      } catch (_) {
        notifier.error('Erro ao buscar categorias.')
        this.categories = []; this.selectedCategory = null
      } finally { this.loadingCategories = false }
    },
    async fetchBrandsByCategory () {
      if (!this.selectedCategory) return

      this.loadingBrands = true

      try {
        this.brands = await api.tikTokShop.getBrands(
          this.account.identifier,
          this.selectedAuthorizedShop.cipher,
          this.selectedCategory.id,
          this.brandSearch
        )
      } catch (_) {
        notifier.error('Erro ao buscar as marcas para a categoria selecionada.')
        this.brands = []; this.selectedBrand = null
      } finally { this.loadingBrands = false }
    },
    async fetchCategoryRules () {
      if (!this.selectedCategory) return
      this.loadingCategoryRules = true
      try {
        this.categoryRules = await api.tikTokShop.getCategoryRules(
          this.account.identifier,
          this.selectedAuthorizedShop.cipher,
          this.selectedCategory.id
        )
        this.initRequirementsFromRules()

        if (this.must('size_chart')) {
          await this.fetchSizeCharts()
        }
      } catch (_) {
        notifier.error('Erro ao buscar as regras específicas da categoria selecionada.')
        this.categoryRules = []
        this.selectedCategory = null
      } finally {
        this.loadingCategoryRules = false
      }
    },
    async fetchSizeCharts () {
      this.loadingSizeCharts = true
      try {
        const response = await api.tikTokShop.getCategorySizeCharts(this.account.identifier)
        this.sizeCharts = (response && response.size_chart) || []

        if (this.requirements.size_chart) {
          const found = this.sizeCharts.find(c => String(c.template_id) === String(this.requirements.size_chart))
          if (found) this.selectedSizeChart = found
        }
      } catch (_) {
        notifier.error('Erro ao buscar as guias de tamanho da loja.')
        this.sizeCharts = []
      } finally {
        this.loadingSizeCharts = false
      }
    },
    async fetchCategoryAttributes () {
      if (!this.selectedCategory) return
      this.loadingAttributes = true

      try {
        this.attributes = await api.tikTokShop.getAttributes(
          this.account.identifier,
          this.selectedAuthorizedShop.cipher,
          this.selectedCategory.id
        )

        this.initProductAttributeShape()
        this.presetCharacteristics()

        this.product.variations.forEach(v => { if (!v.attributes) this.$set(v, 'attributes', {}) })
      } catch (_) {
        notifier.error('Erro ao buscar os atributos da categoria selecionada.')
        this.attributes = []; this.selectedCategory = null
      } finally { this.loadingAttributes = false }
    },
    initProductAttributeShape () {
      if (!this.product.attributes || Array.isArray(this.product.attributes) || typeof this.product.attributes !== 'object') {
        this.$set(this.product, 'attributes', {})
      }

      this.productOnlyAttributes.forEach(attr => {
        const cur = this.product.attributes[attr.id]

        if (attr.isMultipleSelection === true) {
          if (!Array.isArray(cur)) {
            this.$set(this.product.attributes, attr.id, [])
          } else if (cur.length && typeof cur[0] !== 'object') {
            const mapped = cur
              .map(id => (attr.values || []).find(v => String(v.id) === String(id)))
              .filter(Boolean)
            this.$set(this.product.attributes, attr.id, mapped)
          }
        } else {
          if (cur == null) {
            this.$set(this.product.attributes, attr.id, null)
            return
          }
          if (typeof cur === 'object') {
            return
          }
          const found = (attr.values || []).find(v => String(v.id) === String(cur))
          this.$set(this.product.attributes, attr.id, found || null)
        }
      })
    },
    presetCharacteristics () {
      const characteristics = getProductCharacteristics(this.product)

      this.productOnlyAttributes.forEach(attr => {
        const values = attr.values || []

        if (!values.length) {
          const current = this.product.attributes[attr.id]
          const label = getCharacteristicLabel(attr.name, characteristics)

          if (label !== undefined && (current == null || current === '')) {
            this.$set(this.product.attributes, attr.id, label)
          }

          return
        }

        const matched = findCharacteristicValue(attr.name, values.map(value => value.name), characteristics)
        const option = matched !== undefined ? values.find(value => value.name === matched) : undefined

        if (option === undefined) {
          return
        }

        if (attr.isMultipleSelection === true) {
          if (!Array.isArray(attr.value) || !attr.value.length) {
            this.$set(attr, 'value', [option])
          }
        } else if (this.product.attributes[attr.id] == null) {
          this.$set(this.product.attributes, attr.id, option)
        }
      })
    },
    getAttributePlaceholder (attribute) {
      if (!attribute) return 'Informe um valor'

      if (attribute.isMultipleSelection) {
        return `Selecione um ou mais valores de ${attribute.name}`
      }

      if (attribute.values && attribute.values.length) {
        return `Selecione o valor de ${attribute.name}`
      }

      return `Informe o valor de ${attribute.name}`
    },
    formatProduct () {
      this.clearErrors()

      this.product

      const pid = this.product.productId

      const addReq = (property, sku) => {
        this.addError({
          productId: pid,
          ...(sku ? { sku } : {}),
          property,
          error: { message: 'required', value: 1 }
        })
      }

      const isNil = v => v === undefined || v === null
      const isBlank = v => isNil(v) || (typeof v === 'string' && v.trim() === '')
      const isPos = v => typeof v === 'number' && !Number.isNaN(v) && v > 0

      if (!this.selectedAuthorizedShop) addReq('selectedAuthorizedShop')
      if (!this.selectedWarehouse) addReq('selectedWarehouse')
      if (!this.selectedCategory) addReq('selectedCategory')
      if (!this.selectedBrand) addReq('selectedBrand')

      if (isBlank(this.product.title)) addReq('title')
      if (isBlank(this.product.description)) addReq('description')

      if (this.maxTitleLengthDI != null && (this.product.title || '').length > this.maxTitleLengthDI) {
        this.addError({ productId: pid, property: 'title', error: { message: 'maxLength', value: this.maxTitleLengthDI } })
      }
      if (this.maxDescriptionLengthDI != null && (this.product.description || '').length > this.maxDescriptionLengthDI) {
        this.addError({ productId: pid, property: 'description', error: { message: 'maxLength', value: this.maxDescriptionLengthDI } })
      }

      if (!isNil(this.crossDocking) && (Number.isNaN(+this.crossDocking) || +this.crossDocking < 0)) {
        this.addError({ productId: pid, property: 'crossDocking', error: { message: 'invalid', value: 1 } })
      }

      if (this.must('package_dimension')) {
        const pd = this.requirements.package_dimension || {}
        if (!isPos(pd.length)) addReq('requirements.package_dimension.length')
        if (!isPos(pd.width)) addReq('requirements.package_dimension.width')
        if (!isPos(pd.height)) addReq('requirements.package_dimension.height')
        if (!isPos(pd.packageWeight)) addReq('requirements.package_dimension.packageWeight')
        if (isBlank(pd.unit)) addReq('requirements.package_dimension.unit')
      }
      if (this.must('manufacturer') && isBlank(this.requirements.manufacturer)) addReq('requirements.manufacturer')
      if (this.must('epr') && isBlank(this.requirements.epr)) addReq('requirements.epr')
      if (this.must('cod') && isNil(this.requirements.cod)) addReq('requirements.cod')
      if (this.must('size_chart') && isBlank(this.requirements.size_chart)) addReq('requirements.size_chart')

      if (this.must('responsible_person')) {
        const rp = this.requirements.responsible_person || {}
        if (isBlank(rp.name)) addReq('requirements.responsible_person.name')
        if (isBlank(rp.phone)) addReq('requirements.responsible_person.phone')
        if (isBlank(rp.email)) addReq('requirements.responsible_person.email')
      }

      if ((this.productCertRulesRequired || []).length) {
        (this.requirements.product_certifications || []).forEach((c, idx) => {
          const base = `requirements.product_certifications[${idx}]`
          if (isBlank(c.document_details)) addReq(`${base}.document_details`)
          if (c.expiration_date && c.expiration_date.is_required && isBlank(c.expiration_date.value)) {
            addReq(`${base}.expiration_date.value`)
          }
          if (c.sample_image_url && isBlank(c.sample_image_url_user)) {
            addReq(`${base}.sample_image_url_user`)
          }
        })
      }

      const productAttributes = this.productOnlyAttributes.map(attr => {
        if (attr.isRequired) {
          if (attr.isMultipleSelection) {
            if (!Array.isArray(attr.value) || attr.value.length === 0) addReq(String(attr.id))
          } else {
            if (attr.values && attr.values.length) {
              const cur = this.product.attributes[attr.id]
              if (!cur || typeof cur !== 'object') addReq(String(attr.id))
            } else {
              const cur = this.product.attributes[attr.id]
              if (isBlank(cur)) addReq(String(attr.id))
            }
          }
        }

        let valueId = ''
        let valueLabel = ''

        if (attr.isMultipleSelection) {
          const list = Array.isArray(attr.value) ? attr.value : []
          const normalized = list
            .map(o => {
              if (o && typeof o === 'object' && o.id != null) return { id: String(o.id), name: String(o.name ? o.name : o.id) }
              if (o != null) {
                const found = (attr.values || []).find(v => String(v.id) === String(o))
                if (found) return { id: String(found.id), name: String(found.name ? found.name : found.id) }
                return { id: String(o), name: String(o) }
              }
              return null
            })
            .filter(Boolean)

          valueId = normalized.map(o => o.id)
          valueLabel = normalized.map(o => o.name).join(', ')
        } else if (attr.values && attr.values.length) {
          const obj = this.product.attributes[attr.id]
          if (obj && obj.id != null) {
            valueId = String(obj.id)
            valueLabel = String(obj.name ? obj.name : obj.id)
          } else {
            valueId = ''
            valueLabel = ''
          }
        } else {
          const txt = (this.product.attributes[attr.id] ? this.product.attributes[attr.id] : '').toString().trim()
          if (txt) {
            valueId = txt
            valueLabel = txt
          } else {
            valueId = ''
            valueLabel = ''
          }
        }

        return {
          id: String(attr.id),
          label: String(attr.name),
          valueId,
          valueLabel
        }
      })

      const selectedVariations = (this.product.variations || []).filter(v => v && v.selected)

      const attrSelectedSomewhere = new Set(
        this.variationAttributes
          .filter(attr => {
            return selectedVariations.some(variation => {
              const val = (variation.attributes || {})[attr.id]
              if (attr.isMultipleSelection) {
                return Array.isArray(val) && val.length > 0
              }
              if (attr.values && attr.values.length) {
                return !!(val && typeof val === 'object' && val.id != null)
              }
              return !isBlank(val)
            })
          })
          .map(attr => String(attr.id))
      )

      selectedVariations.forEach(variation => {
        const sku = variation.sku || variation.id
        if (!variation.attributes) variation.attributes = {}

        this.variationAttributes.forEach(attr => {
          const requiredForAll = !!attr.isRequired || attrSelectedSomewhere.has(String(attr.id))
          if (!requiredForAll) return

          const val = variation.attributes[attr.id]

          if (attr.isMultipleSelection) {
            if (!Array.isArray(val) || val.length === 0) addReq(String(attr.id), sku)
            return
          }

          if (attr.values && attr.values.length) {
            if (!val || typeof val !== 'object' || val.id == null) addReq(String(attr.id), sku)
            return
          }

          if (isBlank(val)) addReq(String(attr.id), sku)
        })
      })

      const normalizeVarAttrs = (variation) => {
        return this.variationAttributes
          .map(attr => {
            const raw = variation.attributes ? variation.attributes[attr.id] : undefined

            let valueId = ''
            let valueLabel = ''

            if (attr.isMultipleSelection) {
              const list = Array.isArray(raw) ? raw : []
              const normalized = list
                .map(o => {
                  if (o && typeof o === 'object' && o.id != null) {
                    return { id: String(o.id), name: String(o.name ? o.name : o.id) }
                  }
                  if (o != null) {
                    const found = (attr.values || []).find(v => String(v.id) === String(o))
                    return found
                      ? { id: String(found.id), name: String(found.name ? found.name : found.id) }
                      : { id: String(o), name: String(o) }
                  }
                  return null
                })
                .filter(Boolean)

              if (!normalized.length) return null

              valueId = normalized.map(o => o.id)
              valueLabel = normalized.map(o => o.name).join(', ')
            } else if (attr.values && attr.values.length) {
              if (!raw || typeof raw !== 'object' || raw.id == null) return null
              valueLabel = String(raw.name ? raw.name : raw.id)
            } else {
              const txt = (raw == null) ? '' : String(raw).trim()
              if (!txt) return null
              valueId = txt
              valueLabel = txt
            }

            return {
              id: String(attr.id),
              label: String(attr.name),
              valueId,
              valueLabel
            }
          })
          .filter(Boolean)
      }

      const shared = {
        productId: pid,
        title: this.product.title,
        description: this.product.description,
        attributes: productAttributes,
        warehouseId: this.selectedWarehouse.id,
        categoryId: this.selectedCategory ? this.selectedCategory.id : null,
        brand: this.selectedBrand ? this.selectedBrand.name : null,
        brandId: this.selectedBrand ? this.selectedBrand.id : null,
        requirements: JSON.parse(JSON.stringify(this.requirements)),
        account: { identifier: this.account.identifier, shopCipher: this.selectedAuthorizedShop.cipher },
        source: this.fromModule,
        to: this.toModule
      }

      return selectedVariations.map(v => ({
        ...shared,
        sku: v.sku,
        ean: v.ean,
        price: v.price,
        stock: v.stock,
        length: v.length,
        width: v.width,
        height: v.height,
        weight: v.weight,
        images: Array.isArray(v.images) && v.images.length
          ? v.images.map(url => ({ url }))
          : null,
        variationAttributes: normalizeVarAttrs(v),
        original: { product: this.product, variation: v }
      }))
    },
    disabledInputs () {
      return this.product.status === 'sent' || this.product.status === 'error'
    },
    presetPackageDimensions () {
      if (!this.product || !Array.isArray(this.product.variations)) return

      for (const variation of this.product.variations) {
        const width = variation.width
        const height = variation.height
        const weight = variation.weight
        const length = variation.length

        if (!width && !height && !weight && !length) continue

        const pkg = this.requirements.package_dimension

        if (length !== undefined && length !== null) {
          pkg.length = Number(length)
        }

        if (width !== undefined && width !== null) {
          pkg.width = Number(width)
        }

        if (height !== undefined && height !== null) {
          pkg.height = Number(height)
        }

        if (weight !== undefined && weight !== null) {
          pkg.packageWeight = Number(weight)
        }

        break
      }
    }
  },
  computed: {
    ...mapGetters('errors', ['getError']),
    ...mapGetters('module', ['getDefinitionModuleByIdentifierAndType']),
    moduleDefinition () { return this.getDefinitionModuleByIdentifierAndType('marketplace', 'tikTokShop') },
    sending () { return !!this.product.variations.filter(v => v.status === 'sending').length },
    marketplaceVariationProvide () { return 'MarketplaceProvideVariationTikTokShop' },
    everyVariationWithErrorOrSent () { return this.product.variations.every(v => ['error', 'sent'].includes(v.status)) },
    descriptionLength () {
      return { 't-success': this.product.description.length <= this.maxDescriptionLengthDI, 't-error': this.product.description.length > this.maxDescriptionLengthDI }
    },
    titleLength () {
      return { 't-success': this.product.title.length <= this.maxTitleLengthDI, 't-error': this.product.title.length > this.maxTitleLengthDI }
    },
    moduleUploadImageModal () { return { type: this.toModule.type, identifier: this.toModule.identifier } },

    categoryPlaceholder () {
      if (this.categorySearch.length < 3) return 'Realize a busca por categorias'
      if (this.loadingCategories) return 'Carregando categorias...'
      return 'Selecione a categoria do produto'
    },
    brandPlaceholder () {
      if (!this.selectedCategory) return 'Selecione uma categoria para encontrar as marcas'
      if (this.loadingBrands) return 'Carregando marcas...'
      return 'Selecione a marca do produto'
    },
    hasCategoryRequirements () {
      if (!this.categoryRules || !Object.keys(this.categoryRules).length) return false

      const keys = ['cod', 'epr', 'manufacturer', 'package_dimension', 'responsible_person', 'size_chart']
      const anyBasic = keys.some(k => this.must(k))
      const anyCert = ((this.categoryRules.product_certifications || [])
        .some(c => c && c.is_required === true))

      return anyBasic || anyCert
    },
    productCertRulesRequired () {
      const certs = (this.categoryRules && this.categoryRules.product_certifications) || []
      return certs.filter(c => c && c.is_required === true)
    },
    productOnlyAttributes () {
      return (this.attributes || []).filter(a => a.type === 'PRODUCT_PROPERTY')
    },
    variationAttributes () {
      return (this.attributes || []).filter(a => a.type === 'SALES_PROPERTY')
    }
  },
  watch: {
    async account (v) {
      if (v && v.identifier) {
        // Limpa as lojas e centros de distribuição anteriores
        this.authorizedShops = []
        this.selectedAuthorizedShop = null
        this.warehouses = []
        this.selectedWarehouse = null
        await this.getAuthorizedShops()
      }
    },
    async selectedAuthorizedShop (v) { if (v) await this.getWarehouses() },
    selectedSizeChart (v) {
      this.requirements.size_chart = v ? String(v.template_id) : ''
    },
    async selectedCategory (value) {
      if (value) {
        this.fetchCategoryRules()
        this.fetchCategoryAttributes()

        if (this.product.brand) {
          this.brandSearch = this.product.brand
          await this.fetchBrandsByCategory()
        } else {
          await this.fetchBrandsByCategory()
        }
      } else {
        this.selectedBrand = null
        this.brands = []
        this.categoryRules = []
        this.sizeCharts = []
        this.selectedSizeChart = null
        this.loadingSizeCharts = false
        this.attributes = []
      }
    },
    brandSearch (newVal) {
      if (
        newVal &&
        newVal.length >= 3 &&
        this.selectedCategory &&
        !this.selectedBrand
      ) {
        this.handleBrandSearch()
      }
    },
    'product.variations': {
      deep: true,
      immediate: true,
      handler (newVariations) {
        if (!Array.isArray(newVariations)) return

        for (const variation of newVariations) {
          if (!variation.attributes) continue

          const attrs = variation.attributes
          const color = attrs.color
          const size = attrs.size

          if (color && Array.isArray(attrs['100000']) && attrs['100000'].length === 0) {
            attrs['100000'].push({
              id: color,
              name: color,
              custom: true
            })
          } else if (color && !Array.isArray(attrs['100000'])) {
            attrs['100000'] = [
              {
                id: color,
                name: color,
                custom: true
              }
            ]
          }

          if (size && Array.isArray(attrs['100007']) && attrs['100007'].length === 0) {
            attrs['100007'].push({
              id: size,
              name: size,
              custom: true
            })
          } else if (size && !Array.isArray(attrs['100007'])) {
            attrs['100007'] = [
              {
                id: size,
                name: size,
                custom: true
              }
            ]
          }
        }
      }
    },
    requirements: {
      deep: true,
      immediate: true,
      handler (newVal) {
        if (!newVal || !newVal.package_dimension) return

        const pkg = newVal.package_dimension

        const allNull =
          pkg.length === null &&
          pkg.width === null &&
          pkg.height === null &&
          pkg.packageWeight === null

        if (allNull) {
          this.presetPackageDimensions()
        }
      }
    }
  },
  components: {
    FormGroup,
    Multiselect,
    Loader,
    Panel,
    quillEditor,
    ModalAddImage,
    MarketplaceProvideVariationTikTokShop
  }
}
</script>

<style lang="sass" scoped>
.c-box
  margin-bottom: 16px
.c-box__body
  background: #d9edf7
.c-box__heading
  cursor: pointer
  height: 50px
  line-height: 2
  font-size: 15px
  color: #31708f
  background-color: #d9edf7
  border-color: #bde2fb
.icon-circle-down, .icon-circle-up
  font-size: 30px
.c-box__body
  border-color: #bde2fb
.divider
  margin-bottom: 16px
.status
  margin-right: 20px
  margin-top: 4px
.disabledHeader
  cursor: not-allowed
  background-color: #E7E7E7
  border-color: #D3D3D3
  .icon
    opacity: 0.3

.placeholder-msg
  color: #666
  margin: 8px 0

.size-chart-preview
  display: block
  margin-top: 12px
  max-width: 100%
  border: 1px solid #ddd
  border-radius: 4px
</style>



// WEBPACK FOOTER //
// Product.vue?fa1a2c46